"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import { XIcon } from "@phosphor-icons/react/ssr";
import LogoIcon from "@/icons/brand/logo-v2.svg";

import "survey-core/survey-core.css";
import "./style.css";

import type { SurveyData, SurveyPageRuntime, SurveyPageChangingOptions } from "~/types/Survey";
import HTTP_CODES_ENUM from "~/services/api/types/http-codes";
import { STORAGE_KEYS } from "~/types/Storage";

import { useSurveysSaveAnswerService } from "~/services/api/services/questionnaire";
import { sendFacebookEvent } from "~/lib/facebookEvents/FacebookEventsClient";
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { useAuthPatchMeService, } from "~/services/api/services/auth";
import useSafeBackNavigation from "~/hooks/useSafeBackNavigation";
import useAuthActions from "~/services/auth/use-auth-actions";
import useQuizCompleted from "~/hooks/useQuizCompleted";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { useSurveyData } from "~/hooks/useSurveyData";
import useUserIsAuth from "~/hooks/useIsAuth";
import useSkipQuiz from "~/hooks/useSkipQuiz";
import { getFutureDate } from "~/utils/date";
import useUserId from "~/hooks/useUserId";
import useFlag from "~/hooks/useFlag";
import useHasProductInCart from "~/hooks/useHasProductInCart";

import { themeJson } from "./theme";
import { configureProgressBar } from "./progressBar";

import { useQuizStore } from "~/stores/useQuizStore";
import { StorageKeys } from "~/services/StorageKeys";
import { setStorageItem } from "~/lib/storage/Storage";

import { cn } from "~/lib/utils";

import { LIBIDA_PDP } from "~/constants/flags";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { APP_ROUTES } from "~/constants/appRoutes";
import { FUTURE_DAYS } from "~/constants/date";
import { PRODUCT } from "~/constants/products";
import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import { sendGtmEvent } from "~/lib/gtm/gE";
import { trackFxEvent } from "~/lib/optimizely/trackFxEvent";
import { startQuizFxEvent } from "~/lib/optimizely/fxEvents";
import { sendPinterestEvent } from "~/lib/pinterestEvents/PinterestEventsClient";
import { resolveQuizRedirectURL } from "~/utils/resolveQuizRedirect";
import {
    resolveQuizTracking,
    buildWaitlistPayload,
    buildQuizCloseEvent,
    setQuizLocalization,
    useHasFaceCreamInCart,
    useWaitlist,
    SYMPTOMS_LIST_QUESTION,
    DS_WAITLIST_QUESTIONS,
    RETURN_TO_HOME_PAGE_PREFIX,
} from "~/components/features/quiz";

const TYPES_TO_EXCLUDE = ["image", "expression"];

setQuizLocalization();

const customCss = {
    error: {
        root: "sd-error !font-normal",
    },
    progress: "sd-progress !mx-auto !h-2 !max-w-[560px] !rounded-[18px] !mb-2",
    progressBar: "sd-progress__bar !rounded-[18px]",
    progressText: "hidden",
    footer: "quiz-navigation",
    navigation: {
        prev: "sd-btn-prev !w-14 !h-14 !p-0 !shadow-none",
        next: "sd-btn-next !text-white !p-[18px] !shadow-none",
        complete: "sd-btn-complete !text-white !p-[18px] !shadow-none",
    },
};

type Props = {
    category?: string;
    redirectURL?: string;
    miniQuiz?: boolean;
    showHeader?: boolean;
    showPageNumbers?: boolean;
    /** When true, indicates an external component serves as the start page (page 0).
     *  Adjusts page numbering, navigation buttons, and analytics accordingly. */
    hasExternalStartPage?: boolean;
    initialSurveyData?: SurveyData | null;
};

export const Quiz = ({
    category = "",
    redirectURL = APP_ROUTES.PQP_HORMONE_IMBALANCE,
    miniQuiz,
    showHeader = true,
    showPageNumbers = false,
    hasExternalStartPage = false,
    initialSurveyData,
}: Props) => {
    const pageOffset = hasExternalStartPage ? 1 : 0;
    const { surveyData, isLoading } = useSurveyData(category, initialSurveyData);

    const [survey, setSurvey] = useState<Model | null>(null);
    const [pageNo, setPageNo] = useState(0);
    const [preventRedirect, setPreventRedirect] = useState(false);

    const getQuiz = useQuizStore.getState().getQuiz;
    const setSurveyToStore = useQuizStore.getState().setSurvey;

    const router = useRouter();
    const { user, isAuthUser } = useUserIsAuth();
    const { userId } = useUserId();
    const { setUser } = useAuthActions();

    const surveyID = surveyData?.id ?? "";
    const sendToWaitList = useWaitlist(category, surveyID);
    const saveAnswer = useSurveysSaveAnswerService();
    const fetchAuthPatchMe = useAuthPatchMeService();
    const sendBigQueryEvent = useUnauthorizedEventService();
    const sendEvent = useTrackEvent();
    const goBack = useSafeBackNavigation();

    const hasFaceCream = useHasFaceCreamInCart(category);

    const isLibidaQuiz = category === QUIZ_CATEGORIES.LIBIDA;
    const isLibidaPdp = !!useFlag(LIBIDA_PDP) && isLibidaQuiz;
    const hasLibidaInCart = useHasProductInCart([PRODUCT.LIBIDA]);
    const hasLibida = isLibidaPdp && hasLibidaInCart;

    const finalRedirectURL = useMemo(
        () => resolveQuizRedirectURL(redirectURL, {
            hasCheckoutOverrideProduct: hasFaceCream,
            isLibidaPdp,
            hasLibidaInCart,
        }),
        [isLibidaPdp, hasLibidaInCart, hasFaceCream, redirectURL]
    );

    const syncReturnText = useCallback((s: Model) => {
        const name = s.currentPage?.jsonObj?.name ?? "";
        const isReturnPage = name.startsWith(RETURN_TO_HOME_PAGE_PREFIX);

        const nextText = isReturnPage ? "Return to home" : surveyData?.schema?.pageNextText;
        const completeText = isReturnPage ? "Return to home" : surveyData?.schema?.completeText;

        if (nextText && s.pageNextText !== nextText) {
            s.pageNextText = nextText;
            s.render();
        }

        if (completeText && s.completeText !== completeText) {
            s.completeText = completeText;
            s.render();
        }

    }, [surveyData?.schema?.pageNextText, surveyData?.schema?.completeText]);

    const { isCompleted: isQuizDone } = useQuizCompleted(category);
    const shouldSkipQuiz = useSkipQuiz(category);

    const shouldRedirect = isQuizDone || shouldSkipQuiz || (isLibidaPdp && !hasLibida);
    const hasRedirected = useRef(false);

    // Fast path: redirect if quiz is already completed (localStorage or auth)
    useEffect(() => {
        if (preventRedirect || hasRedirected.current || !shouldRedirect) return;

        hasRedirected.current = true;
        router.replace(finalRedirectURL);
    }, [shouldRedirect, preventRedirect, finalRedirectURL, router]);

    // Fetch survey when completion check is done and quiz is not completed
    const handleReturnToHome = useCallback(async (page: SurveyPageRuntime) => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.QUESTIONNAIRE_INTERACTION,
                widget: "questionaire",
                meta_data: {
                    quiz_id: surveyID,
                    click_text: "Return to home",
                },
            },
        });

        const [element] = page.jsonObj?.elements || [];

        if (element?.html) {
            const div = document.createElement("div");
            div.innerHTML = element.html;

            void saveAnswer({
                surveyId: surveyID,
                answer: {
                    userId: userId || "",
                    result: {
                        Conclusion: div.textContent as any,
                    },
                },
            });
        }

        router.push("/");
    }, [router, saveAnswer, sendEvent, surveyID, userId]);

    const handleWaitList = useCallback(async (waitList: string, sender) => {
        await sendToWaitList(waitList, buildWaitlistPayload(waitList, sender));
    }, [sendToWaitList]);

    const handleSurveyComplete = useCallback(async (sender, redirect = true) => {
        const quizCompletedKey = StorageKeys.quiz.completed(category);
        const quizCompletedIdKey = StorageKeys.quiz.completedId(category);
        setStorageItem(quizCompletedKey, "true");
        setStorageItem(quizCompletedIdKey, surveyID);

        if (isAuthUser && user?.id) {
            const { data, status } = await fetchAuthPatchMe({
                completedSurveyId: surveyID,
            });

            if (status === HTTP_CODES_ENUM.OK) {
                if (data?.historyLogs) {
                    const updatedUser = {
                        ...user,
                        historyLogs: data.historyLogs,
                    };
                    setPreventRedirect(true);
                    setUser(updatedUser);
                }
            }

            try {
                await handleWaitList("", sender);
            } catch {
                // Non-blocking: do not block completion redirect on waitlist failure.
            }

            if (redirect) router.push(finalRedirectURL);
        } else if (redirect) {
            const currPageElements: any[] = sender.currentPage.questions ?? [];

            const waitlistElement = currPageElements.find((element) => {
                return element.isVisible && DS_WAITLIST_QUESTIONS.includes(element.jsonObj.name);
            });

            await handleWaitList(waitlistElement ? waitlistElement.name : "", sender);
            router.push(finalRedirectURL);
        }
    }, [router, handleWaitList, fetchAuthPatchMe, user, isAuthUser, setUser, finalRedirectURL, category, surveyID]);

    const surveyComplete = useCallback(async (sender: Model) => {
        const { name } = sender.currentPage.jsonObj;
        const lastActiveQuestion = sender["lastActiveQuestion"]?.jsonObj;
        const lastActiveQuestionAnswer = sender?.data[lastActiveQuestion?.name];
        const adjustedPageNumber = Object.keys(sender?.data || {}).length

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: "questionaire_answer",
                widget: "questionaire",
                meta_data: {
                    quiz_id: surveyID,
                    question_number: adjustedPageNumber,
                    question: lastActiveQuestion?.title,
                    number_of_answers: lastActiveQuestionAnswer?.choices?.length ?? 1,
                    answer_type: lastActiveQuestion?.type,
                    answers: Array.isArray(lastActiveQuestionAnswer)
                        ? lastActiveQuestionAnswer.join("_")
                        : lastActiveQuestionAnswer,
                },
            },
        });

        if (name.startsWith(RETURN_TO_HOME_PAGE_PREFIX)) {
            await handleReturnToHome(sender.currentPage);
        } else {
            await handleSurveyComplete(sender);
        }
    }, [handleReturnToHome, handleSurveyComplete, sendEvent, surveyID]);

    const afterRenderPage = useCallback((survey) => {
        const currentPageNo = survey.currentPageNo;
        const adjustedPageNo = currentPageNo + pageOffset;
        const questions = survey.currentPage.questions;

        setSurveyToStore(category, {
            data: survey.data || {},
            pageNo: survey.currentPageNo,
        });

        questions.forEach((question) => {
            const jsonObj = question.jsonObj;

            if (!TYPES_TO_EXCLUDE.includes(jsonObj.type)) {
                const choices = jsonObj.choices || [];

                sendEvent({
                    event: "user_non_interaction",
                    value: {
                        event_name: "questionaire_impression",
                        widget: "questionaire",
                        meta_data: {
                            quiz_id: surveyID,
                            question_number: adjustedPageNo,
                            question: jsonObj.title || jsonObj.name,
                            number_of_answers: choices?.length,
                            answer_type: jsonObj.type,
                            answers: choices
                                .map((choice) => {
                                    return choice.text || choice;
                                })
                                .join("_"),
                        },
                    },
                });
            } else if (questions.length === 1) {
                sendEvent({
                    event: "user_non_interaction",
                    value: {
                        event_name: "questionaire_impression",
                        widget: "questionaire",
                        meta_data: {
                            quiz_id: surveyID,
                            question_number: adjustedPageNo,
                            question: jsonObj.title || jsonObj.name,
                            number_of_answers: 0,
                            answer_type: "info",
                            answers: [],
                        },
                    },
                });
            }
        });

        if (currentPageNo > 1 && survey.currentPage.showNavigationButtons === false) {
            const [element] = survey.currentPage.jsonObj?.elements || [];

            const div = document.createElement("div");
            div.innerHTML = element.html;

            void saveAnswer({
                surveyId: surveyID,
                answer: {
                    userId: userId || "",
                    result: {
                        Conclusion: div.textContent as any,
                    },
                },
            }).then(() => {
                return handleSurveyComplete(survey, false);
            })
        }

        const { pixels } = resolveQuizTracking(category);

        sendGtmEvent({
            event: "user_interaction",
            value: {
                event_name: "quiz_page_change",
                widget: "questionaire",
                meta_data: {
                    page_number: currentPageNo,
                },
            },
        });

        sendFacebookEvent(sendBigQueryEvent, {
            event_name: `Corner${currentPageNo}`,
        }, pixels);
    }, [surveyID, sendEvent, handleSurveyComplete, saveAnswer, userId, setSurveyToStore, category, pageOffset, sendBigQueryEvent]);

    const currentPageChanged = useCallback((sender, options) => {
        const pageNumber = sender.currentPageNo - 1;
        const adjustedPageNumber = pageNumber + pageOffset;
        const elements = options.oldCurrentPage.jsonObj.elements;

        setPageNo(options.newCurrentPage.visibleIndex);

        const waitlistElement = elements?.find((element) => DS_WAITLIST_QUESTIONS.includes(element.name));
        if (waitlistElement) void handleWaitList(waitlistElement.name, sender);

        syncReturnText(sender);

        const isSymptomsQuestion = elements.some(
            (e) => e.name === SYMPTOMS_LIST_QUESTION
        );

        if (isSymptomsQuestion) {
            setStorageItem(
                category + STORAGE_KEYS.QUIZ_SYMPTOMS,
                sender.data[SYMPTOMS_LIST_QUESTION]
            );
        }

        if (!waitlistElement) elements?.map(async (element) => {
            const choices = element.choices || [];
            const elementName = element.name;
            const elementTitle = element.title?.replace(/\n/g, "");
            const answers = sender.data[elementName];

            if (!answers) return;

            if (options.isNextPage) {
                void saveAnswer({
                    surveyId: surveyID,
                    answer: {
                        userId: userId || "",
                        result: {
                            [elementName]: answers,
                        },
                    },
                });
            }

            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: "questionaire_answer",
                    widget: "questionaire",
                    meta_data: {
                        quiz_id: surveyID,
                        question_number: adjustedPageNumber,
                        question: elementTitle || elementName,
                        number_of_answers: choices.length,
                        answer_type: element.type,
                        answers: Array.isArray(answers)
                            ? answers.join("_")
                            : answers,
                    },
                },
            });
        });

        const { pixels, productName: product_name } = resolveQuizTracking(category);

        if (pageNumber === 0 && !hasExternalStartPage) {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: "quiz_started",
                    widget: "questionaire",
                    meta_data: {
                        quiz_id: surveyID,
                        click_text: "Yes, I’m ready to start",
                    },
                },
            });

            trackFxEvent({ event_name: startQuizFxEvent(product_name) });

            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "Whistle",
            }, pixels);

            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "QuizStarted",
            });

            if (product_name !== "") {
                sendPinterestEvent(sendBigQueryEvent, {
                    event_name: "viewcategory",
                    custom_data: {
                        line_items: [
                            {
                                product_name: product_name,
                            }
                        ]
                    }
                });
            }
        }

        sendGtmEvent({
            event: "user_interaction",
            value: {
                event_name: "quiz_page_change",
                widget: "questionaire",
                meta_data: {
                    page_number: pageNumber + 1,
                },
            },
        });

        sendFacebookEvent(sendBigQueryEvent, {
            event_name: `Corner${pageNumber+1}`,
        }, pixels);
    }, [saveAnswer, sendEvent, surveyID, userId, category, sendBigQueryEvent, syncReturnText, pageOffset, hasExternalStartPage, handleWaitList]);

    // sender: any — SurveyJS Model's currentPage/lastActiveQuestion are private/any in SDK typings
    const currentPageChanging = useCallback(async (sender: any, options: SurveyPageChangingOptions) => {
        const { name } = sender.currentPage.jsonObj;

        if (name.startsWith(RETURN_TO_HOME_PAGE_PREFIX) && options.isNextPage) {
            options.allow = false;
            await handleReturnToHome(sender.currentPage);
        }
    }, [handleReturnToHome]);

    useEffect(() => {
        if (isLoading || !surveyData?.schema || shouldRedirect) return;

        const schema = {
            ...surveyData.schema,
            pages: hasFaceCream ? surveyData.schema.pages.slice(0, -1) : surveyData.schema.pages,
        };

        const newSurvey = new Model(schema);
        const quizState = getQuiz(category);
        const currentSurveyData = quizState?.surveyData || {};
        const currentPageNo = quizState?.pageNo || 0;

        newSurvey.applyTheme(themeJson);
        newSurvey.setVariable("isMarketingCheckboxEnabled", "variant_a");
        configureProgressBar(newSurvey);

        newSurvey.css = customCss;
        newSurvey.data = currentSurveyData || {};
        newSurvey.currentPageNo = currentPageNo;
        newSurvey.checkErrorsMode = "onValueChanged";
        newSurvey.textUpdateMode = "onTyping";

        setPageNo(currentPageNo);
        syncReturnText(newSurvey);
        setSurvey(newSurvey);

        sendEvent({
            event: "user_non_interaction",
            value: {
                event_name: "quiz_viewed",
                widget: "questionaire",
                meta_data: {
                    click_text: null,
                    quiz_id: surveyID,
                },
            },
        });
    }, [surveyData, isLoading, getQuiz, category, hasFaceCream, syncReturnText, sendEvent, surveyID, shouldRedirect]);

    useEffect(() => {
        if (!survey) return;

        survey.onComplete.add(surveyComplete);
        survey.onCurrentPageChanged.add(currentPageChanged);
        survey.onCurrentPageChanging.add(currentPageChanging);

        survey.onAfterRenderPage.add(afterRenderPage);
        survey.setVariable("futureDate", getFutureDate(FUTURE_DAYS));

        return () => {
            survey.onComplete.remove(surveyComplete);
            survey.onCurrentPageChanged.remove(currentPageChanged);
            survey.onCurrentPageChanging.remove(currentPageChanging);
            survey.onAfterRenderPage.remove(afterRenderPage);
        };
    }, [survey, surveyComplete, currentPageChanged, currentPageChanging, afterRenderPage]);

    const handleClose = useCallback(() => {
        sendEvent(buildQuizCloseEvent(survey, surveyID));
        goBack();
    }, [surveyID, survey, sendEvent, goBack]);

    return (
        <div className={cn("quiz-ds", category, miniQuiz && "mini-quiz")}>
            {showHeader && (
                <header className="quiz-header bg-pureWhite w-full fixed top-0 z-10 py-4 pb-6 lg:py-6 px-6">
                    <div className={cn("max-w-contentMd flex items-center justify-between mx-auto")}>
                        <Link href="/" className="logo-quiz">
                            <LogoIcon className="w-[132px] h-[16px] md:w-[166px] md:h-[20px]" />
                        </Link>

                        <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center bg-primary100 text-primary300 hover:bg-primary200 hover:text-primary400 rounded-3xl cursor-pointer transition">
                            <XIcon size={18} weight="bold" />
                        </button>
                    </div>
                </header>
            )}

            {showPageNumbers && survey && (
                <div className="page-numbers text-base font-platform px-4 py-3 bg-softBlue rounded-full tracking-[1.3px]">
                    <span className="page-numbers-text text-trueBlack/40">
                        <span className="text-trueBlack">{pageNo + 1}</span>/{survey.visiblePages.length}
                    </span>
                </div>
            )}

            {survey && <Survey model={survey} />}
        </div>
    );
};

export default memo(Quiz);